<template>
  <v-card class="pa-2 mx-auto" min-width="100" :max-width="MAX_CONTENT_WIDTH">
    <h1 class="page-title">Search</h1>
    <v-divider></v-divider>
    <div class="search-bar-wrapper" :class="{ 'search-bar-dormant': searchBarIsDormant }">
      <v-text-field
        v-model="input.searchTerm"
        class="search-bar"
        @focus="searchInputFocused = true"
        @blur="searchInputFocused = false"
        @keyup.enter="handleEnter"
        @keydown="resetSearchTimer"
        placeholder="search"
        prepend-inner-icon="mdi-magnify"
        outlined
        clearable
        dense
        color="secondary"
        single-line
        hide-details
      ></v-text-field>
    </div>
    <v-select
      v-if="$vuetify.breakpoint.xs"
      v-model="input.category"
      :items="searchCategories.map((item) => ({ text: item.label, value: item.value }))"
      label="category"
      outlined
      dense
      hide-details
      class="mb-2"
      color="secondary"
      item-color="secondary"
    ></v-select>
    <chip-radio v-else v-model="input.category" :options="searchCategories"></chip-radio>
    <v-divider></v-divider>

    <!-- Search Results -->
    <div>
      <div v-for="result in results" :key="(result.medium || 'user') + result.id.toString()">
        <div v-if="input.category === 'users'" class="py-2 pl-3 user-list-entry">
          <router-link :to="{ name: 'profile', params: { username: result.username } }">
            <profile-avatar
              :preset-profile-pic="result.profilePicture"
              :username="result.username"
              :size="48"
            ></profile-avatar>
          </router-link>
          <div class="details">
            <div class="username">
              <router-link :to="{ name: 'profile', params: { username: result.username } }">
                {{ result.username }}
              </router-link>
            </div>
            <div v-if="result.firstName || result.lastName" class="name">
              {{ [result.firstName, result.lastName].join(" ") }}
            </div>
          </div>
          <!-- TODO: Enable this when search includes friendship status -->
          <div v-if="false" class="utility-column">
            <user-utility-button :userId="result.id" :username="result.username" :icon-size="24"></user-utility-button>
          </div>
        </div>

        <media-list-entry
          v-else
          :work-id="result.id"
          :medium="input.category"
          :title="result.title"
          :poster-source="result.posterSource"
          :release-date="result.releaseDate"
          :user-work-relationships="result.userWorkRelationships"
          @change-engaged="handleChangedRelationship(result.id, 'engaged', $event)"
          @change-saved-for-later="handleChangedRelationship(result.id, 'savedForLater', $event)"
          class="ml-2"
        ></media-list-entry>
        <v-divider></v-divider>
      </div>
    </div>

    <div v-if="searchLoading" class="pt-4 pb-2 d-flex justify-center">
      <v-progress-circular indeterminate></v-progress-circular>
    </div>
    <div v-else-if="results.length > 0 && nextToken">
      <button @click="search(true)" class="mx-auto d-block mt-2 load-more">Load More</button>
    </div>
    <div v-if="!searchLoading && !resultsFound" class="pt-4 pb-2">
      <p class="empty-indicator-message">no results found</p>
    </div>
  </v-card>
</template>

<script>
import { mapMutations } from "vuex";

import ChipRadio from "../components/chip-radio.vue";
import MediaListEntry from "../components/media-list-entry.vue";
import ProfileAvatar from "../components/profile-avatar.vue";
import UserUtilityButton from "../components/user-utility-button.vue";

import { MAX_CONTENT_WIDTH } from "../util/constants.js";
import { userFriendlyMediumWord } from "../util/mediumHelpers.js";
import { formatDate } from "../util/dateHelpers.js";
import { mediums } from "../util/enums.js";

const DEFAULT_CATEGORY = mediums.FILM;

export default {
  name: "search-page",

  components: { ChipRadio, MediaListEntry, ProfileAvatar, UserUtilityButton },

  data: () => ({
    MAX_CONTENT_WIDTH,

    searchInputFocused: false,

    input: {
      searchTerm: "",
      // Category needs to start as null to not trigger the watcher on initial set in Mounted
      category: null,
    },

    results: [],
    resultsFound: true,
    timedSearch: undefined,
    searchLoading: false,
    latestTimeOfSearch: 0,
    lastSearchedCategory: "",

    nextToken: "",
  }),

  computed: {
    searchBarIsDormant() {
      return !this.searchInputFocused && !this.input.searchTerm;
    },

    searchCategories() {
      return [
        ...Object.values(this.$mediums).map((medium) => ({
          label: userFriendlyMediumWord(medium, true),
          value: medium,
        })),
        {
          label: "Users",
          value: "users",
        },
      ];
    },
  },

  watch: {
    "input.category": async function (_, oldValue) {
      if (oldValue === null) return;
      this.results.splice(0, this.results.length);
      if (this.input.searchTerm) {
        await this.search(false);
      }
    },
  },

  mounted() {
    this.input.searchTerm = this.$store.state.search.query;
    this.input.category =
      this.$store.state.search.category !== "" ? this.$store.state.search.category : DEFAULT_CATEGORY;
    this.results = this.$store.state.search.results;
    this.$vuetify.goTo(this.$store.state.search.scrollY, { duration: 0 });
  },

  beforeRouteLeave(to, from, next) {
    this.saveSearch({
      query: this.input.searchTerm,
      category: this.input.category,
      results: this.results,
      scrollY: document.documentElement.scrollTop,
    });

    next();
  },

  methods: {
    ...mapMutations(["saveSearch", "showSnackbar"]),
    formatDate,

    async search(loadMore) {
      if (!this.input.searchTerm) {
        return;
      }
      this.searchLoading = true;
      const timeOfSearch = Date.now();

      try {
        const response = await this.$axios.get("/v1/search", {
          params: {
            q: this.input.searchTerm,
            category: this.input.category,
            limit: 20,
            nextToken: loadMore ? this.nextToken : undefined,
          },
        });
        // Safety check needed to ensure that an outdated search does not override the latest in a race-condition
        if (timeOfSearch < this.latestTimeOfSearch) {
          return;
        }
        this.latestTimeOfSearch = timeOfSearch;
        this.nextToken = response.data.nextToken;
        const results = response.data.items;
        this.resultsFound = results.length > 0;

        if (this.input.category === "users") {
          // This map is not needed. It's just used to document available fields
          this.results = results.map((user) => ({
            username: user.username,
            id: user.id,
            profilePicture: user.profilePicture,
            firstName: user.firstName,
            lastName: user.lastName,
          }));
        } else {
          if (!loadMore) {
            this.results = [];
          }
          this.results = this.results.concat(
            results.map((work) => ({
              title: work.title,
              id: work.id,
              posterSource: work.posterSource,
              creators: work.creators,
              userWorkRelationships: work.userInfo
                ? {
                    hasEngaged: work.userInfo.hasEngaged,
                    savedForLater: work.userInfo.savedForLater,
                  }
                : undefined,
              releaseDate: work.releaseDate,
            })),
          );
        }
        this.searchLoading = false;
      } catch (err) {
        console.error(err);
        this.showSnackbar({
          text: "an error occurred",
          error: true,
        });
        this.searchLoading = false;
      }
    },

    resetSearchTimer: function () {
      window.clearTimeout(this.timedSearch);
      this.timedSearch = window.setTimeout(this.search, 500, false);
    },

    handleChangedRelationship(workId, relationship, newStatus) {
      const workToUpdateIndex = this.results.findIndex((item) => item.id === workId);
      if (workToUpdateIndex < 0) return;

      let updatedEntry = this.results[workToUpdateIndex];
      if (relationship === "engaged") {
        updatedEntry.userWorkRelationships.hasEngaged = newStatus;
      } else if (relationship === "savedForLater") {
        updatedEntry.userWorkRelationships.savedForLater = newStatus;
      }

      this.results.splice(
        workToUpdateIndex,
        1,
        // TODO: Improve assignment for remaining UserWOrkRelationships
        updatedEntry,
      );
    },

    async handleEnter() {
      if (this.timedSearch) {
        window.clearTimeout(this.timedSearch);
      }
      await this.search(false);
    },
  },
};
</script>

<style scoped>
.search-bar-wrapper {
  --search-bar-transition: 500ms ease-in-out;

  -webkit-transition: width var(--search-bar-transition);
  -moz-transition: width var(--search-bar-transition);
  -o-transition: width var(--search-bar-transition);
  transition: width var(--search-bar-transition);

  width: 100%;

  margin: 8px auto 12px;
}

.search-bar-dormant {
  -webkit-transition: width var(--search-bar-transition);
  -moz-transition: width var(--search-bar-transition);
  -o-transition: width var(--search-bar-transition);
  transition: width var(--search-bar-transition);

  width: 225px;
}

.search-bar >>> .v-input__prepend-inner {
  -webkit-transition: margin-left var(--search-bar-transition);
  -moz-transition: margin-left var(--search-bar-transition);
  -o-transition: margin-left var(--search-bar-transition);
  transition: margin-left var(--search-bar-transition);
}

.search-bar-dormant .search-bar >>> .v-input__prepend-inner {
  -webkit-transition: margin-left var(--search-bar-transition);
  -moz-transition: margin-left var(--search-bar-transition);
  -o-transition: margin-left var(--search-bar-transition);
  transition: margin-left var(--search-bar-transition);

  margin-left: 55px;
}

.chip-radio-group {
  margin-bottom: 8px;
}

.load-more {
  color: var(--v-text-lighten1);
}

/* TODO: Consider consolidating with styling of media-list-entry */

.user-list-entry {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 8px 8px;
}

.user-list-entry:first-child {
  grid-row: 1 / span 1;
  grid-column: 1 span 1;
}

.details {
  grid-row: 1 / span 1;
  grid-column: 2 / span 1;

  text-align: start;
}
.username {
  font-weight: bold;
}
.user-list-entry .username a {
  color: var(--v-text-base);
}
.name {
  color: var(--v-text-lighten1);
  font-size: 14px;
}

.utility-column {
  grid-row: 1 / span 1;
  grid-column: 3 / span 1;
}

/* Consider reusing this class in other places as well */
.empty-indicator-message {
  text-align: center;
  color: var(--v-text-lighten2);
  font-size: 24px;
  margin-bottom: 0px;
}
</style>
